import httpClient from './httpClient';
import type { PaginatedResult } from '@/types';
import type {
  SupplierListItem,
  SupplierDetail,
  SupplierSearchItem,
  SupplierFilters,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  PurchaseOrderListItem,
  PurchaseOrderDetail,
  PurchaseOrderFilters,
  PurchaseOrderLineDto,
  PurchaseOrderLineInput,
  CreatePurchaseOrderRequest,
  UpdatePurchaseOrderRequest,
  AddPurchaseOrderLineRequest,
} from '@/types/purchasing';

export const purchasingApi = {
  // ── Suppliers ─────────────────────────────────────────────

  async getSuppliers(mdFilters: SupplierFilters): Promise<PaginatedResult<SupplierListItem>> {
    const mdParams: Record<string, string | number> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;

    const mdResponse = await httpClient.get<PaginatedResult<SupplierListItem>>(
      '/suppliers',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getSupplierById(mdId: string): Promise<SupplierDetail> {
    const mdResponse = await httpClient.get<SupplierDetail>(`/suppliers/${mdId}`);
    return mdResponse.data;
  },

  async searchSuppliers(mdQuery: string): Promise<SupplierSearchItem[]> {
    const mdResponse = await httpClient.get<SupplierSearchItem[]>(
      '/suppliers/search',
      { params: { q: mdQuery } }
    );
    return mdResponse.data;
  },

  /** Повертає Guid створеного постачальника */
  async createSupplier(mdRequest: CreateSupplierRequest): Promise<string> {
    const mdResponse = await httpClient.post<string>('/suppliers', mdRequest);
    return mdResponse.data;
  },

  async updateSupplier(mdRequest: UpdateSupplierRequest): Promise<void> {
    await httpClient.put(`/suppliers/${mdRequest.id}`, mdRequest);
  },

  // ── Purchase Orders ───────────────────────────────────────

  async getPurchaseOrders(
    mdFilters: PurchaseOrderFilters
  ): Promise<PaginatedResult<PurchaseOrderListItem>> {
    const mdParams: Record<string, string | number> = {
      page: mdFilters.page,
      pageSize: mdFilters.pageSize,
    };
    if (mdFilters.search) mdParams.search = mdFilters.search;
    if (mdFilters.supplierId) mdParams.supplierId = mdFilters.supplierId;
    if (mdFilters.status) mdParams.status = mdFilters.status;
    if (mdFilters.dateFrom) mdParams.dateFrom = mdFilters.dateFrom;
    if (mdFilters.dateTo) mdParams.dateTo = mdFilters.dateTo;

    const mdResponse = await httpClient.get<PaginatedResult<PurchaseOrderListItem>>(
      '/purchase-orders',
      { params: mdParams }
    );
    return mdResponse.data;
  },

  async getPurchaseOrderById(mdId: string): Promise<PurchaseOrderDetail> {
    const mdResponse = await httpClient.get<PurchaseOrderDetail>(`/purchase-orders/${mdId}`);
    return mdResponse.data;
  },

  /** Повертає Guid створеного замовлення */
  async createPurchaseOrder(mdRequest: CreatePurchaseOrderRequest): Promise<string> {
    const mdResponse = await httpClient.post<string>('/purchase-orders', mdRequest);
    return mdResponse.data;
  },

  async updatePurchaseOrder(mdRequest: UpdatePurchaseOrderRequest): Promise<void> {
    await httpClient.put(`/purchase-orders/${mdRequest.id}`, mdRequest);
  },

  async submitPurchaseOrder(mdId: string): Promise<void> {
    await httpClient.post(`/purchase-orders/${mdId}/submit`);
  },

  async approvePurchaseOrder(mdId: string): Promise<void> {
    await httpClient.post(`/purchase-orders/${mdId}/approve`);
  },

  async cancelPurchaseOrder(mdId: string, mdReason: string): Promise<void> {
    // Сервер звіряє id з тіла з id маршруту
    await httpClient.post(`/purchase-orders/${mdId}/cancel`, { id: mdId, reason: mdReason });
  },

  /** Повертає Guid доданого рядка */
  async addPurchaseOrderLine(
    mdOrderId: string,
    mdLine: Omit<AddPurchaseOrderLineRequest, 'purchaseOrderId'>
  ): Promise<string> {
    const mdRequest: AddPurchaseOrderLineRequest = { ...mdLine, purchaseOrderId: mdOrderId };
    const mdResponse = await httpClient.post<string>(
      `/purchase-orders/${mdOrderId}/lines`,
      mdRequest
    );
    return mdResponse.data;
  },

  async removePurchaseOrderLine(mdOrderId: string, mdLineId: string): Promise<void> {
    await httpClient.delete(`/purchase-orders/${mdOrderId}/lines/${mdLineId}`);
  },

  /**
   * PUT не приймає рядків — приводимо рядки чернетки до стану форми через
   * DELETE/POST lines. Змінений рядок перестворюється (окремого PUT для рядка немає).
   */
  async syncPurchaseOrderLines(
    mdOrderId: string,
    mdOriginalLines: PurchaseOrderLineDto[],
    mdLines: PurchaseOrderLineInput[]
  ): Promise<void> {
    const mdKept = new Set<string>();
    for (const mdLine of mdLines) {
      const mdOriginal = mdLine.id
        ? mdOriginalLines.find((mdO) => mdO.id === mdLine.id)
        : undefined;
      if (
        mdOriginal &&
        mdOriginal.productId === mdLine.productId &&
        mdOriginal.quantityOrdered === mdLine.quantity &&
        mdOriginal.unitPrice === mdLine.unitPrice
      ) {
        mdKept.add(mdOriginal.id);
      }
    }

    for (const mdOriginal of mdOriginalLines) {
      if (!mdKept.has(mdOriginal.id)) {
        await purchasingApi.removePurchaseOrderLine(mdOrderId, mdOriginal.id);
      }
    }

    for (const mdLine of mdLines) {
      if (mdLine.id && mdKept.has(mdLine.id)) continue;
      await purchasingApi.addPurchaseOrderLine(mdOrderId, {
        productId: mdLine.productId,
        quantity: mdLine.quantity,
        unitPrice: mdLine.unitPrice,
      });
    }
  },
};
